import { Link, useSearchParams } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { 
  Droplets, CircleDot, Paintbrush, Wrench, Shield, 
  Sparkles, Gauge, Truck, Car, Bike, ChevronRight,
  Disc, GlassWater, Palette, Check, Star, Crown, Award, Phone, MapPin, Clock, CreditCard
} from 'lucide-react';

// Tarifs détaillés dépannage & remorquage
const DEPANNAGE_TARIFS_JOUR = [
  { distance: '0 – 10 km', leger: 82, lourd: 132 },
  { distance: '10 – 20 km', leger: 133, lourd: 182 },
  { distance: '20 – 30 km', leger: 173, lourd: 232 },
  { distance: '30 – 40 km', leger: 233, lourd: 282 },
  { distance: '40 – 50 km', leger: 283, lourd: 332 },
  { distance: '50 – 60 km', leger: 333, lourd: 382 },
  { distance: '60 – 70 km', leger: 383, lourd: 432 },
  { distance: '70 – 80 km', leger: 433, lourd: 482 },
  { distance: '80 – 90 km', leger: 483, lourd: 532 },
  { distance: '90 – 100 km', leger: 533, lourd: 582 },
  { distance: '> 100 km', leger: '533 + 3,5 €/km', lourd: '582 + 4,5 €/km' },
];

const DEPANNAGE_TARIFS_NUIT = [
  { distance: '0 – 10 km', leger: 152, lourd: 202 },
  { distance: '10 – 20 km', leger: 202, lourd: 252 },
  { distance: '20 – 30 km', leger: 252, lourd: 302 },
  { distance: '30 – 40 km', leger: 302, lourd: 352 },
  { distance: '40 – 50 km', leger: 352, lourd: 402 },
  { distance: '50 – 60 km', leger: 402, lourd: 452 },
  { distance: '60 – 70 km', leger: 452, lourd: 502 },
  { distance: '70 – 80 km', leger: 502, lourd: 552 },
  { distance: '80 – 90 km', leger: 552, lourd: 602 },
  { distance: '90 – 100 km', leger: 602, lourd: 652 },
  { distance: '> 100 km', leger: '602 + 4,5 €/km', lourd: '652 + 5,5 €/km' },
];

// Tarifs dépannages sur place
const DEPANNAGE_SUR_PLACE = [
  { icon: '🔋', intervention: 'Dépannage batterie / démarrage', tarif: 'à partir de 80 – 89 €' },
  { icon: '🔋', intervention: 'Remplacement batterie (pose + batterie incluse)', tarif: 'à partir de 119 – 140 €' },
  { icon: '⛽', intervention: 'Panne sèche (livraison carburant)', tarif: 'à partir de 129 €' },
  { icon: '⛽', intervention: 'Erreur carburant (vidange + remise en route)', tarif: 'à partir de 190 – 229 €' },
  { icon: '🔑', intervention: 'Ouverture de portière', tarif: '37 – 120 €' },
  { icon: '🔑', intervention: 'Ouverture parking sous-sol', tarif: '+20 € en supplément' },
  { icon: '🛞', intervention: 'Crevaison / réparation pneu', tarif: 'à partir de 80 – 130 €' },
  { icon: '🛞', intervention: 'Changement roue de secours', tarif: 'à partir de 109 €' },
  { icon: '🛢️', intervention: 'Absorbant hydrocarbure', tarif: '59 €' },
];

// Prestations de lavage
const LAVAGE_PRESTATIONS = [
  { 
    id: 'velo', 
    name: 'VÉLO', 
    price: 12,
    icon: Droplets,
    color: 'text-green-400',
    bgColor: 'bg-green-500/10',
    features: ['Lavage cadre', 'Nettoyage transmission', 'Rinçage complet'],
    image: 'https://images.pexels.com/photos/12944977/pexels-photo-12944977.jpeg'
  },
  { 
    id: 'clean', 
    name: 'CLEAN', 
    price: 25,
    icon: Droplets,
    color: 'text-blue-400',
    bgColor: 'bg-blue-500/10',
    features: ['Prélavage', 'Lavage carrosserie', 'Jantes', 'Brillant pneus'],
    image: 'https://images.unsplash.com/photo-1761312834150-4beefff097a7?w=400',
    vehicleTypes: ['auto', 'moto']
  },
  { 
    id: 'vip', 
    name: 'VIP', 
    price: 55,
    icon: Star,
    color: 'text-yellow-400',
    bgColor: 'bg-yellow-500/10',
    features: ['Prélavage', 'Lavage carrosserie', 'Jantes', 'Brillant pneus', 'Essuyage intérieur porte', 'Vitres', 'Essuyage plastiques', 'Aspiration', 'Parfum'],
    popular: true,
    image: 'https://images.pexels.com/photos/6872582/pexels-photo-6872582.jpeg',
    vehicleTypes: ['auto']
  },
  { 
    id: 'platine', 
    name: 'PLATINE', 
    price: 75,
    icon: Crown,
    color: 'text-purple-400',
    bgColor: 'bg-purple-500/10',
    features: ['Prélavage', 'Lavage carrosserie', 'Jantes', 'Brillant pneus', 'Essuyage intérieur porte', 'Vitres', 'Essuyage plastiques', 'Aspiration', 'Parfum', 'Shampoing tapis'],
    image: 'https://images.unsplash.com/photo-1760550818554-f4990528bd5a?w=400'
  },
  { 
    id: 'prestige', 
    name: 'PRESTIGE', 
    price: 115,
    icon: Award,
    color: 'text-red-500',
    bgColor: 'bg-red-500/10',
    features: ['Prélavage', 'Lavage carrosserie', 'Jantes', 'Brillant pneus', 'Essuyage intérieur porte', 'Vitres', 'Essuyage plastiques', 'Aspiration', 'Parfum', 'Shampoing tapis', 'Plastiques nourris et ravivés', 'Cire brillant professionnel', 'Protection contre UV, pluie et saletés'],
    image: 'https://images.unsplash.com/photo-1770064747044-0b45514b7ec4?w=400',
    vehicleTypes: ['auto']
  },
];

const SERVICES = [
  { 
    id: 'pneumatique', 
    name: 'Pneumatique', 
    description: 'Montage, équilibrage, réparation de pneus. Vente de pneus toutes marques. Géométrie et parallélisme.',
    category: 'entretien', 
    price_from: null, 
    icon: CircleDot,
    color: 'text-zinc-300',
    bgColor: 'bg-zinc-500/10',
    vehicle_types: ['auto', 'moto']
  },
  { 
    id: 'carrosserie', 
    name: 'Carrosserie & Peinture', 
    description: 'Réparation carrosserie après accident ou accrochage. Peinture complète ou partielle. Débosselage sans peinture.',
    category: 'carrosserie', 
    price_from: null, 
    icon: Paintbrush,
    color: 'text-orange-400',
    bgColor: 'bg-orange-500/10',
    vehicle_types: ['auto', 'moto']
  },
  { 
    id: 'mecanique-auto', 
    name: 'Mécanique Auto', 
    description: 'Révision complète, vidange, changement de filtres. Freins, embrayage, distribution, courroie. Diagnostic électronique. Géométrie & parallélisme. Échappement, direction, suspension, transmission.',
    category: 'reparation', 
    price_from: null, 
    icon: Wrench,
    color: 'text-red-500',
    bgColor: 'bg-red-500/10',
    vehicle_types: ['auto']
  },
  { 
    id: 'climatisation', 
    name: 'Climatisation Auto', 
    description: 'Recharge climatisation, nettoyage circuit, contrôle fonctionnement. Remplacement condenseur, compresseur, canalisation et bouteille filtrante.',
    category: 'reparation', 
    price_from: null, 
    icon: Wrench,
    color: 'text-cyan-500',
    bgColor: 'bg-cyan-500/10',
    vehicle_types: ['auto']
  },
  { 
    id: 'geometrie', 
    name: 'Géométrie & Parallélisme', 
    description: 'Contrôle et réglage de la géométrie des trains roulants. Parallélisme, carrossage, chasse. Réglage point zéro ESP. Améliore la tenue de route et réduit l\'usure des pneus.',
    category: 'reparation', 
    price_from: null, 
    icon: Wrench,
    color: 'text-green-500',
    bgColor: 'bg-green-500/10',
    vehicle_types: ['auto']
  },
  { 
    id: 'vidange-revision', 
    name: 'Vidange & Révision', 
    description: 'Vidange huile moteur, remplacement filtre à huile, filtres air/habitacle/carburant. Mise à niveau des liquides, contrôle 40 points, diagnostic électronique.',
    category: 'reparation', 
    price_from: null, 
    icon: Wrench,
    color: 'text-amber-500',
    bgColor: 'bg-amber-500/10',
    vehicle_types: ['auto']
  },
  { 
    id: 'freinage', 
    name: 'Freinage', 
    description: 'Remplacement plaquettes et disques de frein. Purge liquide de frein. Contrôle système ABS. Intervention sur frein à main et frein de stationnement électrique.',
    category: 'reparation', 
    price_from: null, 
    icon: Wrench,
    color: 'text-rose-500',
    bgColor: 'bg-rose-500/10',
    vehicle_types: ['auto', 'moto']
  },
  { 
    id: 'batterie-demarrage', 
    name: 'Batterie & Démarrage', 
    description: 'Test et remplacement batterie. Diagnostic système de charge. Remplacement alternateur et démarreur. Entretien véhicules électriques et hybrides.',
    category: 'reparation', 
    price_from: null, 
    icon: Wrench,
    color: 'text-yellow-500',
    bgColor: 'bg-yellow-500/10',
    vehicle_types: ['auto', 'moto']
  },
  { 
    id: 'echappement', 
    name: 'Échappement', 
    description: 'Diagnostic et remplacement ligne d\'échappement. Catalyseur, filtre à particules (FAP), silencieux. Réparation fuites et soudures.',
    category: 'reparation', 
    price_from: null, 
    icon: Wrench,
    color: 'text-slate-400',
    bgColor: 'bg-slate-500/10',
    vehicle_types: ['auto', 'moto']
  },
  { 
    id: 'mecanique-moto', 
    name: 'Mécanique Moto & Scooter', 
    description: 'Révision complète, vidange, changement de filtres. Freins, chaîne, embrayage. Diagnostic électronique.',
    category: 'reparation', 
    price_from: null, 
    icon: Wrench,
    color: 'text-orange-500',
    bgColor: 'bg-orange-500/10',
    vehicle_types: ['moto']
  },
  { 
    id: 'jantes', 
    name: 'Réparation Jantes', 
    description: 'Rénovation jantes alu. Réparation impacts et voilages. Peinture et personnalisation de jantes.',
    category: 'carrosserie', 
    price_from: null, 
    icon: Disc,
    color: 'text-purple-400',
    bgColor: 'bg-purple-500/10',
    vehicle_types: ['auto', 'moto']
  },
  { 
    id: 'vitrage', 
    name: 'Vitrage & Pare-brise', 
    description: 'Remplacement pare-brise, vitres latérales et lunette arrière. Réparation d\'impact. Agréé toutes assurances. 🎁 Franchise offerte jusqu\'à 130€ sur tout remplacement de pare-brise ! Nous prenons en charge l\'intégralité des démarches administratives auprès de votre assureur.',
    category: 'carrosserie', 
    price_from: null, 
    icon: GlassWater,
    color: 'text-cyan-400',
    bgColor: 'bg-cyan-500/10',
    vehicle_types: ['auto']
  },
  { 
    id: 'covering', 
    name: 'Covering', 
    description: 'Total covering ou partiel. Film de protection carrosserie PPF. Large choix de couleurs et finitions mat, brillant, satin.',
    category: 'esthetique', 
    price_from: null, 
    icon: Palette,
    color: 'text-pink-400',
    bgColor: 'bg-pink-500/10',
    vehicle_types: ['auto', 'moto']
  },
  { 
    id: 'preparation', 
    name: 'Préparation Esthétique', 
    description: 'Detailing haut de gamme. Polissage, lustrage, traitement céramique. Rénovation optiques. Protection longue durée.',
    category: 'esthetique', 
    price_from: null, 
    icon: Sparkles,
    color: 'text-yellow-400',
    bgColor: 'bg-yellow-500/10',
    vehicle_types: ['auto', 'moto']
  },
  { 
    id: 'cuir', 
    name: 'Réparation Cuir', 
    description: 'Rénovation et réparation sellerie cuir. Nettoyage, coloration, protection. Restauration sièges et volant.',
    category: 'esthetique', 
    price_from: null, 
    icon: Shield,
    color: 'text-amber-600',
    bgColor: 'bg-amber-500/10',
    vehicle_types: ['auto', 'moto']
  },
  { 
    id: 'vitres-teintees', 
    name: 'Vitres Teintées', 
    description: 'Pose de films solaires sur vitres. Protection UV, réduction chaleur, intimité renforcée. Homologué et conforme à la réglementation.',
    category: 'esthetique', 
    price_from: null, 
    icon: Shield,
    color: 'text-slate-400',
    bgColor: 'bg-slate-500/10',
    vehicle_types: ['auto']
  },
  { 
    id: 'controle', 
    name: 'Contrôle Technique', 
    description: 'Nous pourrons nous occuper d\'emmener votre véhicule au contrôle technique chez nos partenaires centre agréé. Contrôle périodique, contre-visite, contrôle pollution.',
    category: 'reparation', 
    price_from: null, 
    icon: Gauge,
    color: 'text-green-400',
    bgColor: 'bg-green-500/10',
    vehicle_types: ['auto']
  },
  { 
    id: 'depannage', 
    name: 'Dépannage & Remorquage', 
    description: 'Intervention rapide 24h/24, 7j/7. Remorquage toutes distances. Dépannage sur place si possible.',
    category: 'urgence', 
    price_from: null, 
    icon: Truck,
    color: 'text-red-400',
    bgColor: 'bg-red-500/10',
    vehicle_types: ['auto', 'moto'],
    featured: true
  },
];

const categories = [
  { id: 'all', name: 'Tous' },
  { id: 'lavage', name: 'Lavage' },
  { id: 'entretien', name: 'Pneumatique' },
  { id: 'reparation', name: 'Mécanique' },
  { id: 'carrosserie', name: 'Carrosserie & Peinture' },
  { id: 'esthetique', name: 'Esthétique' },
  { id: 'urgence', name: 'Dépannage Remorquage' },
];

export default function ServicesPage() {
  const [searchParams] = useSearchParams();
  const categoryParam = searchParams.get('category');
  const [activeCategory, setActiveCategory] = useState(categoryParam || 'all');
  
  useEffect(() => {
    if (categoryParam && categories.some(c => c.id === categoryParam)) {
      setActiveCategory(categoryParam);
    }
  }, [categoryParam]);
  
  const showLavage = activeCategory === 'all' || activeCategory === 'lavage';
  const showDepannage = activeCategory === 'all' || activeCategory === 'urgence';
  const filteredServices = activeCategory === 'all' || activeCategory === 'lavage'
    ? SERVICES 
    : SERVICES.filter(s => s.category === activeCategory);

  return (
    <div className="min-h-screen bg-[#09090B]">
      {/* Hero */}
      <section className="bg-gradient-to-b from-zinc-900 to-[#09090B] py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="text-5xl font-bold text-white mb-4 animate-fade-in">
            NOS <span className="text-red-500">SERVICES</span>
          </h1>
          <p className="text-xl text-zinc-400 max-w-2xl animate-fade-in stagger-1">
            Des solutions complètes pour l'entretien et la réparation de votre véhicule auto ou moto.
          </p>
        </div>
      </section>

      {/* Category Filter */}
      <section className="border-b border-zinc-800 sticky top-20 z-30 bg-[#09090B]/95 backdrop-blur">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex gap-2 py-4 overflow-x-auto">
            {categories.map((cat) => (
              <Button
                key={cat.id}
                variant={activeCategory === cat.id ? 'default' : 'ghost'}
                size="sm"
                onClick={() => setActiveCategory(cat.id)}
                className={activeCategory === cat.id 
                  ? 'bg-red-600 text-white' 
                  : 'text-zinc-400 hover:text-white'
                }
                data-testid={`filter-${cat.id}`}
              >
                {cat.name}
              </Button>
            ))}
          </div>
        </div>
      </section>

      {/* Lavage Prestations */}
      {showLavage && (
        <section className="py-12 border-b border-zinc-800">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center gap-3 mb-8">
              <Droplets className="text-blue-400" size={32} />
              <h2 className="text-3xl font-bold text-white">PRESTATIONS LAVAGE</h2>
            </div>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-6">
              {LAVAGE_PRESTATIONS.map((prestation, idx) => (
                <Card 
                  key={prestation.id}
                  className={`relative bg-zinc-900/50 border-zinc-800 overflow-hidden card-hover animate-fade-in ${
                    prestation.popular ? 'border-yellow-500/50 ring-1 ring-yellow-500/30' : ''
                  }`}
                  style={{ animationDelay: `${idx * 0.1}s` }}
                  data-testid={`lavage-card-${prestation.id}`}
                >
                  {prestation.popular && (
                    <div className="absolute top-0 right-0 bg-yellow-500 text-black text-xs font-bold px-3 py-1 z-10">
                      POPULAIRE
                    </div>
                  )}
                  
                  {/* Image */}
                  <div className="relative h-32 overflow-hidden">
                    <img 
                      src={prestation.image} 
                      alt={prestation.name}
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-zinc-900 to-transparent" />
                  </div>
                  
                  <CardContent className="p-4">
                    <div className="flex items-center gap-3 mb-3 -mt-6 relative z-10">
                      <div className={`w-10 h-10 rounded-xl ${prestation.bgColor} flex items-center justify-center border border-zinc-700`}>
                        <prestation.icon className={prestation.color} size={20} />
                      </div>
                      <div>
                        <h3 className="text-lg font-bold text-white">{prestation.name}</h3>
                        <div className="flex items-center gap-2">
                          <p className="text-2xl font-bold text-red-500">{prestation.price}€</p>
                          {prestation.vehicleTypes?.includes('auto') && prestation.vehicleTypes?.includes('moto') && (
                            <div className="flex items-center gap-1">
                              <Car size={14} className="text-red-500" />
                              <Bike size={14} className="text-red-500" />
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                    
                    <ul className="space-y-1 mb-4">
                      {prestation.features.map((feature, i) => (
                        <li key={i} className="flex items-center gap-2 text-xs text-zinc-300">
                          <Check className="text-green-500 flex-shrink-0" size={12} />
                          <span>{feature}</span>
                        </li>
                      ))}
                    </ul>

                    <Link to="/reservation">
                      <Button size="sm" className={`w-full ${prestation.popular ? 'bg-yellow-500 hover:bg-yellow-600 text-black' : 'bg-red-600 hover:bg-red-700'}`}>
                        Réserver
                      </Button>
                    </Link>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Dépannage & Remorquage Prestations */}
      {showDepannage && (
        <section className="py-12 border-b border-zinc-800">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center gap-3 mb-4">
              <Truck className="text-red-500" size={32} />
              <h2 className="text-3xl font-bold text-white">DÉPANNAGE & REMORQUAGE</h2>
            </div>
            <p className="text-zinc-400 mb-8 flex items-center gap-2">
              <Clock className="text-red-500" size={18} />
              Intervention rapide 24h/24, 7j/7 • Paris et Île-de-France
            </p>
            
            {/* Contact Banner */}
            <div className="bg-gradient-to-r from-red-900/30 via-red-600/20 to-red-900/30 p-4 rounded-lg border border-red-500/30 mb-8">
              <div className="flex flex-col md:flex-row items-center justify-between gap-4">
                <div className="flex items-center gap-3">
                  <Phone className="text-red-500 animate-pulse" size={24} />
                  <div>
                    <p className="text-white font-bold">Besoin d'une intervention urgente ?</p>
                    <p className="text-zinc-400 text-sm">Appelez-nous maintenant !</p>
                  </div>
                </div>
                <a href="tel:0805032032">
                  <Button className="bg-red-600 hover:bg-red-700 text-white font-bold">
                    <Phone size={18} className="mr-2" />
                    0805 032 032
                  </Button>
                </a>
              </div>
            </div>

            {/* Online Booking Banner */}
            <div className="bg-gradient-to-r from-orange-900/30 via-orange-600/20 to-orange-900/30 p-4 rounded-lg border border-orange-500/30 mb-8">
              <div className="flex flex-col md:flex-row items-center justify-between gap-4">
                <div>
                  <p className="text-white font-bold">Réservation en ligne avec acompte</p>
                  <p className="text-zinc-400 text-sm">Frais de déplacement 80€ (déduits de la facture finale)</p>
                </div>
                <Link to="/depannage">
                  <Button className="bg-orange-600 hover:bg-orange-700 text-white font-bold">
                    <CreditCard size={18} className="mr-2" />
                    Réserver en ligne
                  </Button>
                </Link>
              </div>
            </div>
            
            {/* Tarifs de Jour */}
            <div className="mb-8">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-yellow-500/20 rounded-full flex items-center justify-center">
                  <span className="text-xl">☀️</span>
                </div>
                <h3 className="text-xl font-bold text-white">Tarifs de Jour <span className="text-zinc-400 font-normal text-base">(8h – 20h)</span></h3>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full" data-testid="depannage-tarifs-jour">
                  <thead>
                    <tr className="border-b border-zinc-700">
                      <th className="text-left py-3 px-4 text-zinc-400 font-medium">Distance</th>
                      <th className="text-center py-3 px-4 text-zinc-400 font-medium">
                        <div className="flex items-center justify-center gap-2">
                          <Car size={16} className="text-blue-400" />
                          Véhicule &lt; 1,8T
                        </div>
                      </th>
                      <th className="text-center py-3 px-4 text-zinc-400 font-medium">
                        <div className="flex items-center justify-center gap-2">
                          <Truck size={16} className="text-orange-400" />
                          Véhicule &gt; 1,8T
                        </div>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {DEPANNAGE_TARIFS_JOUR.map((row, idx) => (
                      <tr key={idx} className={`border-b border-zinc-800 ${idx % 2 === 0 ? 'bg-zinc-900/30' : ''}`}>
                        <td className="py-3 px-4 text-white font-medium">{row.distance}</td>
                        <td className="py-3 px-4 text-center">
                          <span className="text-green-500 font-bold">
                            {typeof row.leger === 'number' ? `${row.leger} €` : row.leger}
                          </span>
                        </td>
                        <td className="py-3 px-4 text-center">
                          <span className="text-green-500 font-bold">
                            {typeof row.lourd === 'number' ? `${row.lourd} €` : row.lourd}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Tarifs de Nuit */}
            <div className="mb-8">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-blue-500/20 rounded-full flex items-center justify-center">
                  <span className="text-xl">🌙</span>
                </div>
                <h3 className="text-xl font-bold text-white">Tarifs de Nuit <span className="text-zinc-400 font-normal text-base">(20h – 8h)</span></h3>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full" data-testid="depannage-tarifs-nuit">
                  <thead>
                    <tr className="border-b border-zinc-700">
                      <th className="text-left py-3 px-4 text-zinc-400 font-medium">Distance</th>
                      <th className="text-center py-3 px-4 text-zinc-400 font-medium">
                        <div className="flex items-center justify-center gap-2">
                          <Car size={16} className="text-blue-400" />
                          Véhicule &lt; 1,8T
                        </div>
                      </th>
                      <th className="text-center py-3 px-4 text-zinc-400 font-medium">
                        <div className="flex items-center justify-center gap-2">
                          <Truck size={16} className="text-orange-400" />
                          Véhicule &gt; 1,8T
                        </div>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {DEPANNAGE_TARIFS_NUIT.map((row, idx) => (
                      <tr key={idx} className={`border-b border-zinc-800 ${idx % 2 === 0 ? 'bg-zinc-900/30' : ''}`}>
                        <td className="py-3 px-4 text-white font-medium">{row.distance}</td>
                        <td className="py-3 px-4 text-center">
                          <span className="text-green-500 font-bold">
                            {typeof row.leger === 'number' ? `${row.leger} €` : row.leger}
                          </span>
                        </td>
                        <td className="py-3 px-4 text-center">
                          <span className="text-green-500 font-bold">
                            {typeof row.lourd === 'number' ? `${row.lourd} €` : row.lourd}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Dépannages sur place */}
            <div className="mb-8">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-green-500/20 rounded-full flex items-center justify-center">
                  <span className="text-xl">🔧</span>
                </div>
                <h3 className="text-xl font-bold text-white">Dépannages sur Place</h3>
              </div>
              <p className="text-zinc-500 text-sm mb-4">Tarifs moyens constatés — Tous prestataires confondus</p>
              <div className="overflow-x-auto">
                <table className="w-full" data-testid="depannage-sur-place">
                  <thead>
                    <tr className="border-b border-zinc-700">
                      <th className="text-left py-3 px-4 text-zinc-400 font-medium">Intervention</th>
                      <th className="text-right py-3 px-4 text-zinc-400 font-medium">Tarif moyen</th>
                    </tr>
                  </thead>
                  <tbody>
                    {DEPANNAGE_SUR_PLACE.map((row, idx) => (
                      <tr key={idx} className={`border-b border-zinc-800 ${idx % 2 === 0 ? 'bg-zinc-900/30' : ''}`}>
                        <td className="py-3 px-4 text-white">
                          <span className="mr-2">{row.icon}</span>
                          {row.intervention}
                        </td>
                        <td className="py-3 px-4 text-right">
                          <span className="text-green-500 font-bold">{row.tarif}</span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
            
            {/* CTA Devis */}
            <div className="bg-zinc-900/50 p-6 rounded-lg border border-zinc-800 text-center mb-8">
              <p className="text-zinc-400 mb-4">Besoin d'un devis personnalisé ou d'une intervention spécifique ?</p>
              <Link to="/devis">
                <Button className="bg-red-600 hover:bg-red-700 text-white">
                  Demander un devis gratuit
                  <ChevronRight size={18} className="ml-2" />
                </Button>
              </Link>
            </div>
            
            {/* Info supplémentaire */}
            <div className="grid md:grid-cols-3 gap-4">
              <div className="bg-zinc-900/50 p-4 rounded-lg border border-zinc-800">
                <div className="flex items-center gap-2 mb-2">
                  <Car className="text-red-500" size={20} />
                  <h4 className="text-white font-bold">Tous Véhicules</h4>
                </div>
                <p className="text-zinc-400 text-sm">Auto, moto, utilitaire, camping-car</p>
              </div>
              <div className="bg-zinc-900/50 p-4 rounded-lg border border-zinc-800">
                <div className="flex items-center gap-2 mb-2">
                  <Clock className="text-red-500" size={20} />
                  <h4 className="text-white font-bold">Disponible 24h/24</h4>
                </div>
                <p className="text-zinc-400 text-sm">7 jours sur 7, jours fériés inclus</p>
              </div>
              <div className="bg-zinc-900/50 p-4 rounded-lg border border-zinc-800">
                <div className="flex items-center gap-2 mb-2">
                  <MapPin className="text-red-500" size={20} />
                  <h4 className="text-white font-bold">Toute l'Île-de-France</h4>
                </div>
                <p className="text-zinc-400 text-sm">Et au-delà sur demande</p>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Other Services Grid */}
      <section className="py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {(showLavage || showDepannage) && activeCategory === 'all' && (
            <h2 className="text-2xl font-bold text-white mb-8">AUTRES SERVICES</h2>
          )}
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredServices.map((service, idx) => (
              <Card 
                key={service.id} 
                className="bg-zinc-900/50 border-zinc-800 card-hover animate-fade-in"
                style={{ animationDelay: `${idx * 0.05}s` }}
                data-testid={`service-card-${service.id}`}
              >
                <CardContent className="p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div className={`w-14 h-14 rounded-xl ${service.bgColor} flex items-center justify-center`}>
                      <service.icon className={service.color} size={28} />
                    </div>
                    <div className="flex gap-1">
                      {service.vehicle_types.includes('auto') && (
                        <Badge variant="outline" className="border-zinc-700 text-zinc-400">
                          <Car size={12} className="mr-1" /> Auto
                        </Badge>
                      )}
                      {service.vehicle_types.includes('moto') && (
                        <Badge variant="outline" className="border-zinc-700 text-zinc-400">
                          <Bike size={12} className="mr-1" /> Moto
                        </Badge>
                      )}
                    </div>
                  </div>
                  
                  <h3 className="text-xl font-bold text-white mb-2">{service.name}</h3>
                  <p className="text-zinc-400 text-sm mb-4 line-clamp-3">{service.description}</p>
                  
                  <div className="flex items-center justify-between pt-4 border-t border-zinc-800">
                    <div>
                      {service.price_from ? (
                        <>
                          <span className="text-zinc-500 text-sm">À partir de</span>
                          <p className="text-2xl font-bold text-red-500">{service.price_from}€</p>
                        </>
                      ) : (
                        <>
                          <span className="text-zinc-500 text-sm">Tarif</span>
                          <p className="text-2xl font-bold text-red-500">Sur devis</p>
                        </>
                      )}
                    </div>
                    <Link to={service.price_from ? "/reservation" : "/devis"}>
                      <Button size="sm" className="bg-red-600 hover:bg-red-700 text-white">
                        {service.price_from ? "Réserver" : "Devis gratuit"}
                        <ChevronRight size={16} className="ml-1" />
                      </Button>
                    </Link>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-zinc-900/50 border-t border-zinc-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-white mb-4">BESOIN D'UN DEVIS PERSONNALISÉ ?</h2>
          <p className="text-zinc-400 mb-8">Décrivez-nous votre besoin et recevez un devis gratuit sous 24h</p>
          <div className="flex justify-center gap-4">
            <Link to="/devis">
              <Button size="lg" className="bg-red-600 hover:bg-red-700 text-white btn-glow" data-testid="services-quote-btn">
                Demander un devis
              </Button>
            </Link>
            <Link to="/contact">
              <Button size="lg" variant="outline" className="border-zinc-600 text-white hover:bg-zinc-800">
                Nous contacter
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
